import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChartColumnIncreasing, Pencil, Plus, RefreshCw, Search, ShieldCheck } from 'lucide-react'
import { actualizar, enviar, mensajeError, obtener } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { alerta } from '../utils/alertas'
import { Modal } from '../components/Modal'
import type { Permiso, RolDetalle } from '../types'

const rolVacio: RolDetalle = { rolId: 0, nombre: '', descripcion: '', activo: true, permisos: [] }

// Los modulos llegan de la base con su clave tecnica; aqui les damos un nombre entendible.
const NOMBRE_MODULO: Record<string, string> = {
  reportes: 'Analiticas',
  menu: 'Menus',
  caja: 'Caja',
  seg: 'Seguridad'
}

const PERMISO_ANALITICAS = 'reportes.ver'

const etiquetaModulo = (modulo: string) => NOMBRE_MODULO[modulo] ?? modulo

const normalizar = (texto: string) =>
  texto.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

export default function Seguridad() {
  const { puede } = useAuth()
  const [roles, setRoles] = useState<RolDetalle[]>([])
  const [permisos, setPermisos] = useState<Permiso[]>([])
  const [modal, setModal] = useState(false)
  const [cargando, setCargando] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [formulario, setFormulario] = useState<RolDetalle>(rolVacio)
  const editable = puede('roles.editar')

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const [r, p] = await Promise.all([obtener<RolDetalle[]>('roles'), obtener<Permiso[]>('roles/permisos')])
      setRoles(r)
      setPermisos(p)
    } catch (error) {
      await alerta.error('No se pudieron cargar los roles', mensajeError(error))
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  const alternarPermiso = (permisoId: number) =>
    setFormulario((prev) => ({
      ...prev,
      permisos: prev.permisos.includes(permisoId)
        ? prev.permisos.filter((p) => p !== permisoId)
        : [...prev.permisos, permisoId]
    }))

  // Marca o desmarca de golpe un bloque de permisos (un modulo completo o todo lo filtrado).
  const aplicarLote = (ids: number[], activar: boolean) =>
    setFormulario((prev) => ({
      ...prev,
      permisos: activar
        ? [...new Set([...prev.permisos, ...ids])]
        : prev.permisos.filter((p) => !ids.includes(p))
    }))

  const abrirRol = (rol: RolDetalle) => {
    setFormulario({ ...rol, permisos: [...rol.permisos] })
    setBusqueda('')
    setModal(true)
  }

  const guardar = async () => {
    if (!formulario.nombre.trim()) {
      await alerta.error('Falta el nombre', 'Escribe un nombre para el rol.')
      return
    }
    try {
      const cuerpo = { ...formulario, tenantId: 1 }
      if (formulario.rolId > 0) await actualizar(`roles/${formulario.rolId}`, cuerpo)
      else await enviar('roles', cuerpo)
      setModal(false)
      await cargar()
      void alerta.toast('Rol guardado')
    } catch (error) {
      await alerta.error('No se pudo guardar el rol', mensajeError(error))
    }
  }

  const idAnaliticas = permisos.find((p) => p.clave === PERMISO_ANALITICAS)?.permisoId

  const grupos = useMemo(() => {
    const filtro = normalizar(busqueda.trim())
    const visibles = filtro
      ? permisos.filter((p) =>
          [p.clave, p.descripcion ?? '', etiquetaModulo(p.modulo)].some((t) => normalizar(t).includes(filtro))
        )
      : permisos
    return [...new Set(visibles.map((p) => p.modulo))].map((modulo) => ({
      modulo,
      items: visibles.filter((p) => p.modulo === modulo)
    }))
  }, [permisos, busqueda])

  const idsVisibles = grupos.flatMap((g) => g.items.map((p) => p.permisoId))

  return (
    <div className="anima-entrada">
      <div className="fila-entre mb-4">
        <div>
          <h2 className="m-0 text-xl font-bold">Roles y permisos</h2>
          <p className="texto-suave mt-1">Define que puede hacer cada perfil dentro del sistema.</p>
        </div>
        <div className="fila">
          <button className="btn btn-plano btn-icono mr-15" onClick={() => void cargar()}>
            <RefreshCw size={17} className={cargando ? 'anima-pulso' : ''} />
          </button>
          {editable && (
            <button className="btn" onClick={() => abrirRol(rolVacio)}>
              <Plus size={17} /> Nuevo rol
            </button>
          )}
        </div>
      </div>

      <div className="grid-2">
        {roles.map((r) => (
          <div key={r.rolId} className="tarjeta anima-entrada">
            <div className="fila-entre">
              <div className="fila">
                <ShieldCheck size={18} />
                <strong>{r.nombre}</strong>
              </div>
              <button className="btn btn-secundario btn-sm" onClick={() => abrirRol(r)}>
                <Pencil size={14} />
              </button>
            </div>
            <p className="texto-suave mt-2">{r.descripcion ?? 'Sin descripcion'} - {r.permisos.length} permiso(s)</p>
            <div className="fila mt-2 flex-wrap">
              {idAnaliticas !== undefined && (
                <span className={r.permisos.includes(idAnaliticas) ? 'etiqueta etiqueta-info' : 'etiqueta'}>
                  <ChartColumnIncreasing size={13} />
                  {r.permisos.includes(idAnaliticas) ? 'Analiticas' : 'Sin analiticas'}
                </span>
              )}
              {!r.activo && <span className="etiqueta etiqueta-danger">Inactivo</span>}
            </div>
          </div>
        ))}
      </div>

      <Modal
        abierto={modal}
        titulo={formulario.rolId > 0 ? 'Editar rol' : 'Nuevo rol'}
        ancho={760}
        onCerrar={() => setModal(false)}
        pie={
          <>
            <button className="btn btn-plano" onClick={() => setModal(false)}>Cancelar</button>
            <button className="btn" onClick={() => void guardar()} disabled={!editable}>Guardar</button>
          </>
        }
      >
        <div className="grid-2">
          <div className="campo">
            <label htmlFor="rolNombre">Nombre</label>
            <input id="rolNombre" value={formulario.nombre} onChange={(e) => setFormulario({ ...formulario, nombre: e.target.value })} />
          </div>
          <div className="campo">
            <label htmlFor="rolDesc">Descripcion</label>
            <input id="rolDesc" value={formulario.descripcion ?? ''} onChange={(e) => setFormulario({ ...formulario, descripcion: e.target.value })} />
          </div>
        </div>

        <label className="fila mt-2 gap-2">
          <input
            type="checkbox"
            checked={formulario.activo}
            onChange={(e) => setFormulario({ ...formulario, activo: e.target.checked })}
            className="h-4.5 w-4.5"
          />
          Rol activo
        </label>

        <div className="fila-entre mt-3.5 flex-wrap">
          <div className="campo mb-0 flex-1 min-w-56">
            <div className="fila">
              <Search size={16} className="texto-suave" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar acceso (ej. analiticas)"
                aria-label="Buscar acceso"
              />
            </div>
          </div>
          <div className="fila">
            <span className="texto-suave">{formulario.permisos.length} de {permisos.length}</span>
            <button className="btn btn-plano btn-sm" onClick={() => aplicarLote(idsVisibles, true)}>Marcar</button>
            <button className="btn btn-plano btn-sm" onClick={() => aplicarLote(idsVisibles, false)}>Limpiar</button>
          </div>
        </div>

        {grupos.length === 0 && <p className="texto-suave mt-3.5">Ningun acceso coincide con la busqueda.</p>}

        {grupos.map(({ modulo, items }) => {
          const ids = items.map((p) => p.permisoId)
          const marcados = ids.filter((id) => formulario.permisos.includes(id)).length
          return (
            <div key={modulo} className="mt-3.5">
              <label className="fila gap-2">
                <input
                  type="checkbox"
                  checked={marcados === ids.length}
                  ref={(el) => {
                    if (el) el.indeterminate = marcados > 0 && marcados < ids.length
                  }}
                  onChange={(e) => aplicarLote(ids, e.target.checked)}
                  className="h-4.5 w-4.5"
                />
                <strong className="capitalize">{etiquetaModulo(modulo)}</strong>
                <span className="texto-suave">{marcados}/{ids.length}</span>
              </label>
              <div className="grid-2 mt-2">
                {items.map((p) => (
                  <label key={p.permisoId} className="fila texto-suave gap-2" title={p.clave}>
                    <input
                      type="checkbox"
                      checked={formulario.permisos.includes(p.permisoId)}
                      onChange={() => alternarPermiso(p.permisoId)}
                      className="h-4.5 w-4.5"
                    />
                    {p.descripcion ?? p.clave}
                  </label>
                ))}
              </div>
            </div>
          )
        })}
      </Modal>
    </div>
  )
}
